import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Container, Button, Form, Row, Col, Modal } from 'react-bootstrap';
import Masonry from 'react-masonry-css';
import ecardApi from '../../api/eCards/ecardApi';
import { FaPaperPlane, FaGift, FaWhatsapp } from 'react-icons/fa';
import SpinnerLoader from '../../components/Buttons/SpinnerLoader';
import { useDebounce } from '../../hooks/useDebounce';
import { useAuthContext } from '../../context/AuthContext';
import { handleApiResponse, showApiMessage } from '../../modals/global/ApiMessageModal/ApiMessageModal';
import './admin-ecards.css';
import '../../pages/styles/e-card.css';

const AdminEcards = () => {
  const { currentEvent } = useAuthContext();
  const [items, setItems] = useState([]);
  const [cardType, setCardType] = useState('all'); // 'all', 'Invitation', 'Contribution'
  const [eventType, setEventType] = useState('all'); // specific event type
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearchTerm = useDebounce(searchInput, 500);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [showDiscountModal, setShowDiscountModal] = useState(false);

  const eventTitle = currentEvent?.event_title || currentEvent?.name || '';

  const cardTypeEventMap = {
    'Invitation': ['all', 'Wedding', 'SendOff', 'KitchenParty', 'Engagement', 'Anniversary'],
    'Contribution': ['all', 'Wedding', 'SendOff', 'KitchenParty']
  };

  const fetchECards = React.useCallback(async (currentPage = 1, search = '', cType = 'all', eType = 'all') => {
    try {
      if (search || cType !== 'all' || eType !== 'all' || currentPage > 1) {
        setSearching(true);
      } else if (currentPage === 1) {
        setLoading(true);
      }

      const params = {
        page: currentPage,
      };
      if (search) params.search = search;
      if (cType !== 'all') params.card_category = cType;
      if (eType !== 'all') params.event_type = eType;

      const response = await ecardApi.searchTemplates(params);

      let data = response.templates || [];

      // Sort items: those with event_id first
      data.sort((a, b) => {
        const aHasEvent = a.event_id && a.event_id !== "" ? 1 : 0;
        const bHasEvent = b.event_id && b.event_id !== "" ? 1 : 0;
        return bHasEvent - aHasEvent;
      });

      if (currentPage === 1) {
        setItems(data);
      } else {
        setItems(prev => {
          const existingIds = new Set(prev.map(i => i.id));
          return [...prev, ...data.filter(i => !existingIds.has(i.id))];
        });
      }
      setPagination(response.pagination);
    } catch (error) {
      console.error(error);
      handleApiResponse(error);
    } finally {
      setLoading(false);
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    setPage(1);
    fetchECards(1, searchTerm, cardType, eventType);
  }, [searchTerm, cardType, eventType, fetchECards]);

  useEffect(() => {
    if (debouncedSearchTerm !== searchTerm) {
      setSearchTerm(debouncedSearchTerm);
    }
  }, [debouncedSearchTerm, searchTerm]);

  const handleCardTypeChange = (type) => {
    setCardType(type);
    setEventType('all');
  };

  const handleEventTypeChange = (type) => {
    setEventType(type);
  };

  const handleSearchChange = (event) => {
    setSearchInput(event.target.value);
  };

  const loadMore = () => {
    if (pagination && page < pagination.total_pages) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchECards(nextPage, searchTerm, cardType, eventType);
    }
  };

  // Items are already filtered by backend
  const filteredItems = items;

  const availableEventTypes = cardType === 'all'
    ? []
    : (cardTypeEventMap[cardType] || []);

  const breakpointColumnsObj = {
    default: 4,
    1100: 3,
    700: 2,
    500: 2
  };

  return (
    <Container>
      <div className="row">
        <div className="col-12">
          <br />
          <h1 className="stylish-heading text-center" style={{ color: '#4169e1' }}>Ecards </h1>
          <p className="text-muted"></p>

          <div className="dashboard row mt-4">
            <div className="dashboard col text-center">
              <Link to="/Generated-WhatsApp-Card" className="text-decoration-none">
                <div className="dashboard bg-dash p-4 rounded-lg">
                  <div className="dashText">
                    <FaPaperPlane className="mb-2" size={24} />
                    <div>Generated Cards</div>
                  </div>
                </div>
              </Link>
            </div>

            <div className="dashboard col text-center">
              <Link to="/whatsapp-card" className="text-decoration-none">
                <div className="dashboard bg-dash p-4 rounded-lg">
                  <div className="dashText">
                    <FaWhatsapp className="mb-2" size={24} />
                    <div>WhatsApp Messages</div>
                  </div>
                </div>
              </Link>
            </div>

            <div className="dashboard col text-center">
              <Link to="#" onClick={(e) => { e.preventDefault(); setShowDiscountModal(true); }} className="text-decoration-none">
                <div className="dashboard bg-dash p-4 rounded-lg">
                  <div className="dashText">
                    <FaGift className="mb-2" size={24} />
                    <div>Discount</div>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </div>
      <br />

      <Row className="mb-4">
        <Col xs={12} className="mb-4">
          <div className="ecard-filters-wrapper">
            {/* Card Type Segmented Control */}
            <div className="ecard-segmented-control mb-4">
              <div
                className={`segment-btn ${cardType === 'all' ? 'active' : ''}`}
                onClick={() => handleCardTypeChange('all')}
              >
                All Types
              </div>
              <div
                className={`segment-btn ${cardType === 'Invitation' ? 'active' : ''}`}
                onClick={() => handleCardTypeChange('Invitation')}
              >
                Invitation
              </div>
              <div
                className={`segment-btn ${cardType === 'Contribution' ? 'active' : ''}`}
                onClick={() => handleCardTypeChange('Contribution')}
              >
                Contribution
              </div>
            </div>

            {/* Event Type Selection */}
            {cardType !== 'all' && availableEventTypes.length > 0 && (
              <div className="ecard-event-filters mt-4 pt-3" style={{ borderTop: '1px dashed #e9ecef' }}>
                <h6 className="event-filter-title text-center mb-3 text-muted">Select Event Type</h6>
                <div className="d-flex flex-wrap justify-content-center gap-2">
                  {availableEventTypes.map(event => (
                    <div
                      key={event}
                      className={`event-pill-btn ${eventType === event ? 'active' : ''}`}
                      onClick={() => handleEventTypeChange(event)}
                    >
                      {event === 'all' ? 'All Events' : event}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Col>

        <Col xs={12} md={12}>
          <Form className="search-form">
            <div className="search-input-container">
              <Form.Control
                type="text"
                placeholder="Search by Name, Color or Event type"
                value={searchInput}
                onChange={handleSearchChange}
                className="search-input mx-auto"
                size="sm"
                style={{ maxWidth: '600px', width: '100%', paddingRight: '100px' }}
              />
              {searching && (
                <div style={{ position: 'absolute', right: '50%', transform: 'translate(280px, -50%)', top: '50%', color: '#6c757d', fontSize: '0.85rem', zIndex: 10 }}>
                  Searching...
                </div>
              )}
            </div>
          </Form>
        </Col>
      </Row>

      {loading && page === 1 ? (
        <SpinnerLoader />
      ) : items.length === 0 ? (
        <div className="text-center py-5">
          <h4>No cards found</h4>
          <p className="text-muted">Try adjusting your filters or search terms.</p>
        </div>
      ) : (
        <>
          <Masonry
            breakpointCols={breakpointColumnsObj}
            className="my-masonry-grid"
            columnClassName="my-masonry-grid_column"
          >
            {filteredItems.map(item => {
              const priceInt = parseInt(item.price) || 0;
              const imgUrl = item.photo_path || item.lg_path || item.md_path || item.wallpaper;
              const isAssigned = item.event_id && item.event_id !== "";

              return (
                <div key={item.id} className="card-wrapper position-relative">
                  {isAssigned && (
                    <div style={{
                      position: 'absolute',
                      top: '15px',
                      right: '10px',
                      backgroundColor: '#10b981',
                      color: 'white',
                      padding: '4px 12px',
                      borderRadius: '20px',
                      fontSize: '0.80rem',
                      fontWeight: 'bold',
                      zIndex: 3,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}>
                      ★ Assigned to Event
                    </div>
                  )}
                  <div className="price-tab d-flex align-items-center gap-2">
                    <span>TZS {priceInt.toLocaleString()}</span>
                    {priceInt >= 750 && (
                      <FaWhatsapp className="text-white" size={16} title="WhatsApp Support Available" />
                    )}
                  </div>
                  <Link to={`/ecards/${item.id}`} state={{ card: item }}>
                    <img src={imgUrl} alt={item.name} className="card-img" />
                  </Link>
                </div>
              );
            })}
          </Masonry>

          {pagination && page < pagination.total_pages && (
            <div className="text-center mt-4 mb-5">
              <Button
                variant="primary"
                onClick={loadMore}
                disabled={searching || loading}
                style={{ padding: '10px 30px', borderRadius: '30px' }}
              >
                {(searching && page > 1) ? 'Loading...' : 'Load More'}
              </Button>
            </div>
          )}
        </>
      )}
      <Modal
        show={showDiscountModal}
        onHide={() => setShowDiscountModal(false)}
        size="md"
        centered
      >
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="w-100 text-center fw-bold mt-3" style={{ color: '#4169e1', fontSize: '1.4rem', letterSpacing: '-0.3px' }}>
            Discount Conditions
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4 pb-4">
          <p className="text-muted text-center mb-4" style={{ fontSize: '0.9rem' }}>
            Mteja anapata discount kwenye kadi pindi anapokidhi vigezo vifuatavyo:
          </p>
          <ul style={{ paddingLeft: '1.25rem', fontSize: '0.9rem', color: '#334155', lineHeight: '1.7' }}>
            <li className="mb-3">
              Awe ametumia mfumo kwa muda mrefu kuanzia kutuma meseji za taarifa na michango.
            </li>
            <li className="mb-3">
              Awe amenunua meseji za matumizi kisichopungua kiasi cha shilingi laki moja (<strong>TZS 100,000</strong>).
            </li>
            <li className="mb-3">
              Discount ni kwenye kadi tu; kwenye SMS credits na WhatsApp credits haihusiki.
            </li>
          </ul>
          <div className="d-flex justify-content-center gap-3 mt-4">
            <a
              href={`https://wa.me/255747636516?text=${encodeURIComponent(`Habari! Naomba Discount kwenye matumizi yangu ya kadi kupitia Harusi Online kwa shughuli ya: ${eventTitle}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-success rounded-pill px-3 py-2 d-inline-flex align-items-center gap-2 fw-semibold"
              style={{ boxShadow: '0 4px 12px rgba(40, 167, 69, 0.2)' }}
            >
              <FaWhatsapp size={16} /> Request on WhatsApp
            </a>
            <Button
              onClick={() => setShowDiscountModal(false)}
              style={{
                backgroundColor: '#64748b',
                border: 'none',
                borderRadius: '25px',
                padding: '8px 30px',
                fontWeight: '600',
                boxShadow: '0 4px 12px rgba(100, 116, 139, 0.2)'
              }}
            >
              Close
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default AdminEcards;